import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import { Days } from 'types';
import { UserAvailability } from 'core/interfaces/userInterface';
import { ShiftRequest } from 'types/shift-requests';

const WEEKDAY_LABELS = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
] as const;

const DAY_LABEL_TO_MOMENT: Record<string, number> = {
	Sunday: 0,
	Monday: 1,
	Tuesday: 2,
	Wednesday: 3,
	Thursday: 4,
	Friday: 5,
	Saturday: 6,
	sun: 0,
	sunday: 0,
	mon: 1,
	monday: 1,
	tue: 2,
	tues: 2,
	tuesday: 2,
	wed: 3,
	wednesday: 3,
	thu: 4,
	thur: 4,
	thurs: 4,
	thursday: 4,
	fri: 5,
	friday: 5,
	sat: 6,
	saturday: 6,
};

const normalizeDayLabelKey = (value?: string | null): string =>
	(value || '').trim().toLowerCase();

// API days use ISO weekday ids (Monday=1 ... Sunday=7). JS/moment use Sunday=0.
const WEEK_DAYS = WEEKDAY_LABELS.map((label, momentIndex) => ({
	label,
	position: String(momentIndex),
	id: momentIndex === 0 ? 7 : momentIndex,
}));

export type WeekDay = Days;

export type WeekDayRef = {
	'@id'?: string;
	id?: number | string;
	position?: string | number;
	label?: string;
};

export type AvailabilityTimeRange = {
	day?: string | { '@id'?: string; id?: number | string } | null;
	startAt?: string | null;
	endAt?: string | null;
};

const toPlaceholderDay = (dayInfo: (typeof WEEK_DAYS)[number]): WeekDay => ({
	'@id': `/api/days/${dayInfo.id}`,
	'@type': 'Day',
	id: dayInfo.id,
	label: dayInfo.label,
	locale: dayInfo.label.toLowerCase(),
	position: dayInfo.position,
});

const TIME_PARSE_FORMATS = [
	moment.ISO_8601,
	'YYYY-MM-DDTHH:mm:ss.SSSSSSZ',
	'YYYY-MM-DDTHH:mm:ss.SSSZ',
	'YYYY-MM-DDTHH:mm:ssZ',
	'YYYY-MM-DDTHH:mm:ss.SSSSSS',
	'YYYY-MM-DDTHH:mm:ss.SSS',
	'YYYY-MM-DDTHH:mm:ss',
	'YYYY-MM-DD HH:mm:ss',
	'HH:mm:ss.SSSSSS',
	'HH:mm:ss.SSS',
	'HH:mm:ss',
	'H:mm:ss',
	'HH:mm',
	'H:mm',
];

export const normalizeDayIri = (day?: string | { '@id'?: string } | null): string => {
	if (!day) return '';
	const raw = typeof day === 'string' ? day : day['@id'] || '';
	return raw.replace(/^\//, '');
};

export const isSameDayIri = (
	left?: string | { '@id'?: string } | null,
	right?: string | { '@id'?: string } | null
): boolean => {
	const leftIri = normalizeDayIri(left);
	const rightIri = normalizeDayIri(right);
	if (!leftIri || !rightIri) return false;
	return leftIri === rightIri || leftIri.endsWith(rightIri) || rightIri.endsWith(leftIri);
};

export const extractDayNumericId = (dayRef: unknown): number | null => {
	if (dayRef == null || dayRef === '') return null;
	if (typeof dayRef === 'number' && Number.isFinite(dayRef)) return dayRef;
	if (typeof dayRef === 'string') {
		const match = dayRef.trim().match(/(\d+)$/);
		return match ? Number(match[1]) : null;
	}
	if (typeof dayRef === 'object') {
		const day = dayRef as { id?: number | string; '@id'?: string };
		if (typeof day.id === 'number' && Number.isFinite(day.id)) return day.id;
		if (day.id != null) return extractDayNumericId(String(day.id));
		if (day['@id']) return extractDayNumericId(day['@id']);
	}
	return null;
};

export const toMomentWeekday = (dayId: number | null | undefined): number | null => {
	if (dayId == null || !Number.isFinite(dayId)) return null;
	if (dayId === 7 || dayId === 0) return 0;
	if (dayId >= 1 && dayId <= 6) return dayId;
	return null;
};

export const getMomentWeekdayFromDay = (day?: WeekDayRef | null): number | null => {
	if (!day) return null;
	const labelKey = normalizeDayLabelKey(day.label);
	if (labelKey && DAY_LABEL_TO_MOMENT[labelKey] !== undefined) {
		return DAY_LABEL_TO_MOMENT[labelKey];
	}
	const localeKey = normalizeDayLabelKey((day as { locale?: string }).locale);
	if (localeKey && DAY_LABEL_TO_MOMENT[localeKey] !== undefined) {
		return DAY_LABEL_TO_MOMENT[localeKey];
	}
	const position = day.position != null ? Number(day.position) : NaN;
	if (position >= 0 && position <= 6) return position;
	return toMomentWeekday(extractDayNumericId(day));
};

export const availabilityRefersToDay = (
	availabilityDay: unknown,
	day: WeekDayRef
): boolean => {
	if (availabilityDay == null || availabilityDay === '') return false;
	if (isSameDayIri(availabilityDay as string | { '@id'?: string }, day['@id'])) return true;
	if (day.id != null && isSameDayIri(availabilityDay as string | { '@id'?: string }, `/api/days/${day.id}`)) {
		return true;
	}

	const availId = extractDayNumericId(availabilityDay);
	const targetId = extractDayNumericId(day);
	if (availId != null && targetId != null && availId === targetId) return true;

	const availabilityAsDay =
		typeof availabilityDay === 'object' && availabilityDay
			? (availabilityDay as WeekDayRef & { locale?: string })
			: { '@id': String(availabilityDay) };

	if (typeof availabilityDay === 'object' && availabilityDay) {
		const availLabel =
			normalizeDayLabelKey(availabilityAsDay.label) ||
			normalizeDayLabelKey(availabilityAsDay.locale);
		const targetLabel = normalizeDayLabelKey(day.label);
		if (availLabel && targetLabel && availLabel === targetLabel) return true;
	}

	const availabilityWeekday = getMomentWeekdayFromDay(availabilityAsDay);
	const targetWeekday = getMomentWeekdayFromDay(day);
	return availabilityWeekday != null && availabilityWeekday === targetWeekday;
};

export const findWeekDayForMomentIndex = <T extends WeekDayRef>(
	days: T[] | null | undefined,
	momentIndex: number
): T | null => (days || []).find((day) => getMomentWeekdayFromDay(day) === momentIndex) || null;

export const parseAvailabilityMoment = (value?: string | null): moment.Moment | null => {
	if (value == null || value === '') return null;
	const raw = String(value).trim();
	if (!raw) return null;

	const parsed = moment(raw, TIME_PARSE_FORMATS, true);
	if (parsed.isValid()) return parsed;

	// Backend sometimes returns fractional seconds beyond SSS (e.g. 09:00:00.000000)
	const timeOnly = raw.match(/^(\d{1,2}:\d{2}(?::\d{2})?)/);
	if (timeOnly) {
		const trimmed = moment(timeOnly[1], ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'H:mm'], true);
		if (trimmed.isValid()) return trimmed;
	}

	const fallback = moment(raw);
	return fallback.isValid() ? fallback : null;
};

export const formatAvailabilityTime = (value?: string | null): string => {
	const parsed = parseAvailabilityMoment(value);
	return parsed ? parsed.format('h:mm A') : '';
};

/**
 * Stored `user_availabilities` rows mean the day is available. Equal clock times
 * (same day or next day) are a 24-hour window — e.g. 05:00→05:00. Unavailable
 * days are omitted from the collection (deleted), not stored as equal-time markers.
 *
 * Shift-request "clear this day" UX still uses equal times via
 * {@link getRequestAvailabilityTimes}, not this helper.
 */
export const isUnavailableAvailabilityRange = (
	_startAt?: string | null,
	_endAt?: string | null,
): boolean => false;

export const buildAllWeekDays = (daysData?: Days[] | null): WeekDay[] => {
	if (!daysData?.length) {
		return WEEK_DAYS.map(toPlaceholderDay);
	}

	const existingDaysMap = new Map<string, Days>();
	daysData.forEach((day) => {
		const labelKey = normalizeDayLabelKey(day.label);
		const localeKey = normalizeDayLabelKey(day.locale);
		if (labelKey) existingDaysMap.set(labelKey, day);
		if (localeKey) existingDaysMap.set(localeKey, day);
	});

	return WEEK_DAYS.map((dayInfo) => {
		const existingDay = existingDaysMap.get(normalizeDayLabelKey(dayInfo.label));
		if (existingDay) return existingDay;
		return toPlaceholderDay(dayInfo);
	});
};

export const getLatestAvailabilityForDay = <T extends AvailabilityTimeRange>(
	currentAvailability: T[] | undefined,
	day: WeekDayRef
): T | null => {
	const matching = (currentAvailability || []).filter((avail) => {
		if (!avail.day) return false;
		return availabilityRefersToDay(avail.day, day);
	});

	if (!matching.length) return null;

	return matching.reduce((latest, current) => {
		if (!latest) return current;
		const latestStart = parseAvailabilityMoment(latest.startAt);
		const currentStart = parseAvailabilityMoment(current.startAt);
		if (!latestStart) return current;
		if (!currentStart) return latest;
		return currentStart.isAfter(latestStart) ? current : latest;
	}, matching[0]);
};

export type AvailabilityKind = 'available' | 'unavailable' | 'unset';
export type AvailabilityChangeKind = 'unchanged' | 'changed' | 'added' | 'removed';

export const getAvailabilityTimeLabel = (
	latestAvail: UserAvailability | null,
	hasAnyAvailabilityInWeek: boolean
): { label: string; kind: AvailabilityKind } => {
	if (!latestAvail) {
		if (hasAnyAvailabilityInWeek) {
			return { label: 'Not available', kind: 'unavailable' };
		}

		return { label: 'Not set', kind: 'unset' };
	}

	if (isUnavailableAvailabilityRange(latestAvail.startAt, latestAvail.endAt)) {
		return { label: 'Not available', kind: 'unavailable' };
	}

	const startTime = formatAvailabilityTime(latestAvail.startAt);
	const endTime = formatAvailabilityTime(latestAvail.endAt);

	return { label: `${startTime} – ${endTime}`, kind: 'available' };
};

export const describeAvailabilityForDay = (
	latestAvail: UserAvailability | null,
	dayName: string,
	hasAnyAvailabilityInWeek: boolean
): { displayText: string; isUnavailable: boolean; isNotSet: boolean } => {
	const { label, kind } = getAvailabilityTimeLabel(latestAvail, hasAnyAvailabilityInWeek);

	return {
		displayText: `${dayName} → ${label}`,
		isUnavailable: kind === 'unavailable',
		isNotSet: kind === 'unset',
	};
};

export const orderWeekDaysFromDate = (
	days: WeekDay[],
	weekStart?: moment.MomentInput | null
): WeekDay[] => {
	if (!weekStart || !days.length) return days;

	const startDay = moment(weekStart).day();
	const startIndex = days.findIndex((day) => Number(day.position) === startDay);
	if (startIndex <= 0) return days;

	return [...days.slice(startIndex), ...days.slice(0, startIndex)];
};

export type EmployeeWeekAvailabilityRow = {
	dayLabel: string;
	dayShort: string;
	label: string;
	kind: AvailabilityKind;
};

export const buildEmployeeWeekAvailabilityRows = (
	currentAvailability: UserAvailability[] | undefined,
	days?: Days[] | null,
	weekStart?: moment.MomentInput | null
): EmployeeWeekAvailabilityRow[] => {
	const weekDays = orderWeekDaysFromDate(buildAllWeekDays(days), weekStart);
	const availability = currentAvailability || [];
	const hasAnyAvailabilityInWeek = availability.length > 0;

	return weekDays.map((day) => {
		const { label, kind } = getAvailabilityTimeLabel(
			getLatestAvailabilityForDay(availability, day),
			hasAnyAvailabilityInWeek
		);

		return {
			dayLabel: day.label,
			dayShort: day.label.slice(0, 3),
			label,
			kind,
		};
	});
};

export type AvailabilityComparisonRow = {
	dayId: string;
	dayLabel: string;
	dayShort: string;
	currentLabel: string;
	currentKind: AvailabilityKind;
	requestedLabel: string;
	requestedKind: AvailabilityKind | 'none';
	hasRequest: boolean;
	changeKind: AvailabilityChangeKind;
};

const getRequestedAvailabilityLabel = (
	req?: ShiftRequest | null
): { label: string; kind: AvailabilityKind | 'none' } => {
	if (!req) {
		return { label: '—', kind: 'none' };
	}

	const { startValue, endValue, enabled } = getRequestAvailabilityTimes(req);
	if (enabled) {
		return {
			label: `${formatAvailabilityTime(startValue)} – ${formatAvailabilityTime(endValue)}`,
			kind: 'available',
		};
	}

	return { label: 'Not available', kind: 'unavailable' };
};

const getAvailabilityChangeKind = (
	currentKind: AvailabilityKind,
	currentLabel: string,
	requestedKind: AvailabilityKind | 'none',
	requestedLabel: string,
	hasRequest: boolean
): AvailabilityChangeKind => {
	if (!hasRequest) return 'unchanged';
	if (currentLabel === requestedLabel) return 'unchanged';
	if (currentKind !== 'available' && requestedKind === 'available') return 'added';
	if (currentKind === 'available' && requestedKind !== 'available') return 'removed';
	return 'changed';
};

export const buildAvailabilityComparisonRows = (
	days: WeekDay[],
	currentAvailability: UserAvailability[] | undefined,
	relatedRequests: ShiftRequest[]
): AvailabilityComparisonRow[] => {
	const availability = currentAvailability || [];
	const hasAnyAvailabilityInWeek = availability.length > 0;

	return days.map((day) => {
		const current = getAvailabilityTimeLabel(
			getLatestAvailabilityForDay(availability, day),
			hasAnyAvailabilityInWeek
		);
		const dayRequest = findAvailabilityRequestForDay(relatedRequests, day);
		const requested = getRequestedAvailabilityLabel(dayRequest);
		const hasRequest = Boolean(dayRequest);

		return {
			dayId: day['@id'] || String(day.id),
			dayLabel: day.label,
			dayShort: day.label.slice(0, 3),
			currentLabel: current.label,
			currentKind: current.kind,
			requestedLabel: requested.label,
			requestedKind: requested.kind,
			hasRequest,
			changeKind: getAvailabilityChangeKind(
				current.kind,
				current.label,
				requested.kind,
				requested.label,
				hasRequest
			),
		};
	});
};

export const toDayjsAvailabilityTime = (value?: string | null): Dayjs | null => {
	const parsed = parseAvailabilityMoment(value);
	if (!parsed) return null;
	return dayjs().hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0);
};

const asRequestList = (
	allRequests?: ShiftRequest[] | { member?: ShiftRequest[]; 'hydra:member'?: ShiftRequest[] }
): ShiftRequest[] => {
	if (!allRequests) return [];
	if (Array.isArray(allRequests)) return allRequests;
	return allRequests.member || allRequests['hydra:member'] || [];
};

export const collectRelatedAvailabilityRequests = (
	allRequests: ShiftRequest[] | { member?: ShiftRequest[]; 'hydra:member'?: ShiftRequest[] } | undefined,
	currentRequest: ShiftRequest
): ShiftRequest[] => {
	const employeeId = currentRequest.employeeRequest?.id || currentRequest.createdBy?.id;
	const related = asRequestList(allRequests).filter((req) => {
		const isAvailability = req.type?.label?.toLowerCase().includes('availability');
		if (!isAvailability) return false;

		const reqEmployeeId = req.employeeRequest?.id || req.createdBy?.id;
		if (employeeId && reqEmployeeId && reqEmployeeId !== employeeId) return false;

		const isPending =
			req.employeeRequestConfirmed === null || req.employeeRequestConfirmed === undefined;
		return isPending || req.id === currentRequest.id;
	});

	if (!related.some((req) => req.id === currentRequest.id)) {
		return [...related, currentRequest];
	}

	return related;
};

export const findAvailabilityRequestForDay = (
	requests: ShiftRequest[],
	day: WeekDayRef
): ShiftRequest | undefined =>
	requests.find((req) => availabilityRefersToDay(req.day, day));

export const getRequestAvailabilityTimes = (
	req?: ShiftRequest | null
): { startValue: string | null; endValue: string | null; enabled: boolean } => {
	if (!req) {
		return { startValue: null, endValue: null, enabled: false };
	}

	const startValue = req.startAt || req.dayStartTime || null;
	const endValue = req.endAt || req.dayEndTime || null;
	const start = parseAvailabilityMoment(startValue);
	const end = parseAvailabilityMoment(endValue);
	// Shift requests still use equal clock times as an "unavailable that day" marker.
	const isUnavailable = Boolean(
		start && end && start.format('HH:mm') === end.format('HH:mm') && start.isSame(end, 'day')
	);
	const enabled = Boolean(start && end && !isUnavailable);

	return { startValue, endValue, enabled };
};
